<template>
  <div
    class="travel-date-picker"
    v-click-outside="closeCalendar">
    <button
      class="selected-date"
      @click="toggleCalendar">
      <span class="travel-date-picker__display-date">
        <svg class="travel-date-picker__calendar-icon">
          <use :xlink:href="`#${calendar}`" />
        </svg>
        <div class="travel-date-picker__display-text">
          <span class="selected-date__label">
            {{ $t('search.departureDate') }}
          </span>
          {{ formattedDate }}
        </div>
      </span>
    </button>
    <div
      v-if="showCalendar"
      class="travel-date-picker__calendar-overlay">
      <v-date-picker
        ref="datepicker"
        v-model="selectedDate"
        @update:model-value="handleDateSelect"
        :columns="1"
        :locale="getCurrentLocale()"
        class="vc-custom"
        :min-date="minDate"
        :timezone="'UTC'"
        :initial-page="initialPage"
        :masks="{
          title: 'MMMM YYYY',
          weekdays: 'WWW',
          input: ['L', 'DD.MM.YYYY', 'DD.MM.YY'],
          dayPopover: 'WWW, MMM D, YYYY',
        }">
        <template #nav-left-button="{ move }">
          <button
            type="button"
            class="vc-arrow-left"
            @click="() => move()">
            <i class="vc-arrow-left" />
          </button>
        </template>
        <template #nav-right-button="{ move }">
          <button
            type="button"
            class="vc-arrow vc-next"
            @click="() => move()">
            <i class="vc-arrow-right" />
          </button>
        </template>
      </v-date-picker>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import type { Component, Directive } from 'vue'
  import { DatePicker } from 'v-calendar'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { format } from 'date-fns'
  import calendar from '@/assets/icons/calendar.svg'
  import { getCurrentLocale } from '@/i18n'
  import { formatDateShort } from '@/utils/dateFormatters'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'

  defineOptions({
    name: 'TravelDate',
  })

  // Cast to the generic Component type: v-calendar's DatePicker has an enormous
  // inferred type that, left intact, makes the component type too large for TS
  // to serialize (TS7056). Aliased so the template's <v-date-picker> resolves.
  const VDatePicker = DatePicker as Component

  const vClickOutside: Directive<HTMLElement, () => void> = {
    beforeMount(el, binding) {
      ;(el as any).clickOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value()
        }
      }
      document.body.addEventListener('click', (el as any).clickOutsideEvent)
    },
    unmounted(el) {
      document.body.removeEventListener('click', (el as any).clickOutsideEvent)
    },
  }

  const travelSearchStore = useTravelSearchStore()
  const showCalendar = ref(false)
  const selectedDate = ref<Date | null>(null)
  const datepicker = ref<any>(null)

  const minDate = computed(() => new Date())

  const initialPage = computed(() => {
    let dateToUse: Date

    if (travelSearchStore.departureDate) {
      dateToUse = new Date(travelSearchStore.departureDate)
    } else {
      dateToUse = new Date()
    }

    return {
      month: dateToUse.getMonth() + 1,
      year: dateToUse.getFullYear(),
    }
  })

  const formattedDate = computed(() => {
    if (selectedDate.value) {
      return formatDateShort(selectedDate.value)
    }
    return formatDateShort(minDate.value)
  })

  const toggleCalendar = () => {
    showCalendar.value = !showCalendar.value
  }

  const closeCalendar = () => {
    showCalendar.value = false
  }

  const handleDateSelect = (date: Date) => {
    selectedDate.value = date
    showCalendar.value = false
    travelSearchStore.setDepartureDate(format(date, 'yyyy-MM-dd'))
    pushInteractionToDatalayer('select date', { selection: format(date, 'yyyy-MM-dd') })
  }

  watch(
    () => travelSearchStore.departureDate,
    (newDepartureDate) => {
      selectedDate.value = newDepartureDate ? new Date(newDepartureDate) : null
    },
    { immediate: true },
  )
</script>

<style lang="scss" scoped>
  .travel-date-picker {
    display: flex;
    flex-direction: column;
    position: relative;

    .selected-date {
      @include search-field-surface;
      display: flex;
      align-items: center;
      gap: $search-field-icon-gap;
      padding: 0 $search-field-padding-x;
      text-align: left;
      cursor: pointer;

      .modal-overlay & {
        min-width: 149px;
      }

      &__label {
        @include search-field-label;
      }
    }

    &__calendar-icon {
      @include search-field-icon;
      flex-shrink: 0;
    }

    &__display-date {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: $search-field-icon-gap;
    }

    &__display-text {
      text-align: left;
      white-space: nowrap;
    }

    &__calendar-overlay {
      position: absolute;
      top: 100%;
      z-index: 1000;
      left: 95%;
      transform: translateX(-50%);
      background-color: white;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

      @media (max-width: $breakpoint-xs) {
        right: initial;
        top: 0;
        left: 85%;
      }
    }

    :deep(.vc-custom) {
      .vc-highlight {
        background-color: #2a347a !important;
        border-radius: 0 !important;
        width: 32px !important;
        height: 32px !important;

        &.vc-day-content {
          color: white !important;
          background-color: #2a347a !important;
          border-radius: 0 !important;
        }
      }

      // Header styles
      .vc-header {
        color: #2a347a;

        .vc-title {
          color: #2a347a;
          background: transparent !important;

          &:hover {
            background: transparent !important;
          }
        }

        .vc-arrow {
          background: transparent !important;
        }

        button {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }

      // Calendar title button (month/year)
      .vc-title-wrapper {
        button {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }

      // Navigation buttons
      .vc-arrow-left,
      .vc-arrow-right,
      button.vc-nav-arrow {
        width: 26px !important;
        height: 26px !important;
        background: transparent !important;
        border: none !important;
        padding: 4px !important;
        font-size: 20px !important;
        color: #2a347a !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        &:hover {
          background: transparent !important;
        }
      }

      // Month/year popover styles
      .vc-popover-content {
        * {
          background: white !important;
          &:hover {
            background: #f4f5f8 !important;
          }
        }

        .vc-nav-title {
          background: transparent !important;
        }

        button {
          background: transparent !important;
          &:hover {
            background: #f4f5f8 !important;
          }
        }
      }

      // Force transparent background on all navigation elements
      [class*='vc-'] {
        &[class*='-arrow'],
        &[class*='-button'],
        &[class*='-nav'],
        &[class*='-title'] {
          background: transparent !important;
          &:hover {
            background: transparent !important;
          }
        }
      }
    }
  }
</style>
